
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Album } from "@/lib/types";
import { Share2, Copy, Globe, Lock, Image as ImageIcon, Plus } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";

interface AlbumGridProps {
  albums: Album[];
  loading: boolean;
  onDelete: (id: string) => void;
  onAddImages?: (albumId: string) => void;
}

const AlbumGrid = ({ albums, loading, onDelete, onAddImages }: AlbumGridProps) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="glass-card overflow-hidden">
            <div className="aspect-video bg-black/30 overflow-hidden relative">
              <Skeleton className="h-full w-full absolute" />
            </div>
            <CardContent className="p-3">
              <Skeleton className="h-5 w-3/4 mb-2" />
              <Skeleton className="h-4 w-1/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (albums.length === 0) {
    return (
      <div className="text-center py-8 my-4">
        <div className="mb-3 text-white/60">
          <ImageIcon className="mx-auto h-12 w-12 opacity-50" />
        </div>
        <h3 className="text-lg font-medium">No albums yet</h3>
        <p className="text-white/60 mt-1 mb-4">
          Create your first album to organize your images
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {albums.map((album) => (
        <AlbumCard 
          key={album.id} 
          album={album} 
          onDelete={onDelete}
          onAddImages={onAddImages}
        />
      ))}
    </div>
  );
};

interface AlbumCardProps {
  album: Album;
  onDelete: (id: string) => void;
  onAddImages?: (albumId: string) => void;
}

const AlbumCard = ({ album, onDelete, onAddImages }: AlbumCardProps) => {
  const [copied, setCopied] = useState(false);

  // Generate share URL with origin to ensure it works in production
  const shareUrl = `${window.location.origin}/album/${album.slug}`;

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    navigator.clipboard.writeText(shareUrl);
    toast.success("Album link copied to clipboard");
    
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If Web Share API is available, use it
    if (navigator.share) {
      try {
        await navigator.share({
          title: album.name,
          text: album.description || `Check out this album: ${album.name}`,
          url: shareUrl,
        });
        toast.success("Shared successfully");
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error("Error sharing:", error);
          // Fall back to copy if sharing failed for reasons other than user cancellation
          handleCopyLink(e);
        }
      }
    } else {
      // Fall back to copy if Web Share API is not available
      handleCopyLink(e);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (confirm("Are you sure you want to delete this album?")) {
      onDelete(album.id);
    }
  };

  const handleAddImages = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (onAddImages) {
      onAddImages(album.id);
    }
  };

  const imageCount = album.images?.length || 0;

  return (
    <Link to={`/album/${album.slug}`}>
      <Card className="glass-card overflow-hidden h-full transition-transform duration-200 hover:scale-[1.02] hover:shadow-lg">
        <div className="aspect-video bg-black/30 overflow-hidden relative flex items-center justify-center">
          {album.images && album.images.length > 0 ? (
            <div className="grid grid-cols-2 gap-1 p-2 w-full h-full">
              {album.images.slice(0, 4).map((image, idx) => (
                <div key={image.id} className={`bg-black/20 ${album.images!.length === 1 ? 'col-span-2 row-span-2' : ''}`}>
                  <img src={image.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-white/40">
              <ImageIcon size={48} />
            </div>
          )}
          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1">
            {album.isPublic ? (
              <>
                <Globe size={12} />
                <span>Public</span>
              </>
            ) : (
              <>
                <Lock size={12} />
                <span>Private</span>
              </>
            )}
          </div>
        </div>
        <CardContent className="p-3 flex items-center justify-between">
          <div className="truncate mr-2">
            <h3 className="font-medium truncate">{album.name}</h3>
            <p className="text-sm text-white/60 truncate">
              {imageCount} {imageCount === 1 ? 'image' : 'images'} • {album.views} {album.views === 1 ? 'view' : 'views'}
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            {onAddImages && (
              <Button
                size="sm"
                variant="ghost"
                className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/20"
                onClick={handleAddImages}
                title="Add images"
              >
                <Plus size={16} />
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/20"
              onClick={handleShare}
              title="Share"
            >
              <Share2 size={16} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full hover:bg-black/20"
              onClick={handleCopyLink}
              title="Copy link"
            >
              <Copy size={16} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
              onClick={handleDelete}
              title="Delete"
            >
              ×
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default AlbumGrid;
